/**
 * 侧边栏导航配置 - 统一数据源
 * @description 所有导航功能的唯一数据源，包括侧边栏、标题管理、面包屑等
 * @author Mech3D Team
 * @date 2025-08-11
 * @version 4.0.0 - 统一架构版
 */

import { PERMISSIONS } from '@/constants/permissions'

/**
 * 面包屑项接口
 */
export interface BreadcrumbItem {
  title: string
  path?: string
  current?: boolean
}

/**
 * 分层权限配置
 */
export interface LayeredPermissions {
  /** 页面级权限 */
  page: string[]
  /** 功能级权限映射 */
  functions?: Record<string, string[]>
  /** 权限引导配置 */
  guidance?: {
    missingPermission?: string
    upgradeMessage?: string
    benefitPreview?: string[]
    upgradePaths?: Array<{
      role: string
      path: string
      time: string
      benefits?: string
    }>
  }
}

/**
 * 导航元数据
 */
export interface NavigationMeta {
  description: string
  requiresAuth: boolean
  order: number
  category: 'workspace' | 'account' | 'identity' | 'model' | 'task' | 'finance' | 'system' | 'service'
  pageType?: 'dashboard' | 'business' | 'development'
  hasTabPages?: boolean
  isStatusDriven?: boolean
  isDevelopmentOnly?: boolean
  /** 集成分层权限配置 */
  layeredPermissions?: LayeredPermissions
}

/**
 * 侧边栏导航项
 */
export interface SidebarNavigationItem {
  /** 唯一标识 */
  key: string
  /** 页面标题 */
  title: string
  /** 路由路径 */
  path: string
  /** 图标名称 */
  icon: string
  /** 基础权限要求 */
  permissions: string[]
  /** 预配置面包屑 */
  breadcrumb: BreadcrumbItem[]
  /** 元数据 */
  meta: NavigationMeta
  /** 子导航项 */
  children?: SidebarNavigationItem[]
}

/**
 * 导航分组配置
 */
export interface NavigationGroup {
  key: string
  title: string
  icon: string
  order: number
  description?: string
}

/**
 * 侧边栏导航配置 - 统一数据源
 * @description 所有导航功能统一使用此配置
 */
export const SIDEBAR_USER_NAVIGATION: SidebarNavigationItem[] = [
  // 0. v2首页 - 公共页面
  {
    key: 'v2-home',
    title: 'v2首页',
    path: '/home',
    icon: 'House',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: 'v2首页', current: true },
    ],
    meta: {
      description: 'v2版本首页，展示新版HomeBanner组件',
      requiresAuth: false,
      order: 0,
      category: 'workspace',
      pageType: 'dashboard',
    },
  },

  // 1. 用户中心首页
  {
    key: 'dashboard',
    title: '用户中心',
    path: '/user',
    icon: 'House',
    permissions: [],
    breadcrumb: [],
    meta: {
      description: '个人工作台概览，快速了解账户状态和业务数据',
      requiresAuth: true,
      order: 1,
      category: 'workspace',
      pageType: 'dashboard',
    },
  },

  // 2-4. 个人账号管理 (3个独立页面)
  {
    key: 'profile',
    title: '个人信息',
    path: '/user/profile',
    icon: 'User',
    permissions: [PERMISSIONS.USER.PROFILE.VIEW],
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '个人账号管理' },
      { title: '个人信息', current: true },
    ],
    meta: {
      description: '个人资料展示和基础操作入口',
      requiresAuth: true,
      order: 2,
      category: 'account',
    },
  },
  {
    key: 'settings',
    title: '账号设置',
    path: '/user/settings',
    icon: 'Setting',
    permissions: [PERMISSIONS.USER.SETTINGS.VIEW],
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '个人账号管理' },
      { title: '账号设置', current: true },
    ],
    meta: {
      description: '账号安全设置、偏好配置(保留4标签页)',
      requiresAuth: true,
      order: 3,
      category: 'account',
      hasTabPages: true,
    },
  },
  {
    key: 'messages',
    title: '消息中心',
    path: '/user/messages',
    icon: 'ChatLineSquare',
    permissions: [PERMISSIONS.USER.MESSAGES.VIEW],
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '个人账号管理' },
      { title: '消息中心', current: true },
    ],
    meta: {
      description: '统一消息管理，系统通知、私信、任务消息',
      requiresAuth: true,
      order: 4,
      category: 'account',
    },
  },

  // 5-8. 身份认证管理 - 重构为2级导航（4个独立页面）
  {
    key: 'designer-registration',
    title: '设计师注册',
    path: '/user/designer/registration',
    icon: 'EditPen',
    permissions: [], // 🔧 修复：移除权限要求，允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '设计师认证' },
      { title: '设计师注册', current: true },
    ],
    meta: {
      description: '3步简化注册流程，成为专业设计师',
      requiresAuth: true,
      order: 5,
      category: 'identity',
    },
  },
  {
    key: 'designer-center',
    title: '设计师中心',
    path: '/user/designer/center',
    icon: 'Edit',
    permissions: [], // 🔧 修复：移除权限要求，允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '设计师认证' },
      { title: '设计师中心', current: true },
    ],
    meta: {
      description: '4标签管理中心，专注资料管理和服务设置',
      requiresAuth: true,
      order: 6,
      category: 'identity',
      isStatusDriven: true,
      hasTabPages: true,
    },
  },
  {
    key: 'provider-registration',
    title: '服务商注册',
    path: '/user/provider/registration',
    icon: 'OfficeBuilding',
    permissions: [], // 🔧 修复：移除权限要求，允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '服务商认证' },
      { title: '服务商注册', current: true },
    ],
    meta: {
      description: '6步企业注册流程，加入服务商网络',
      requiresAuth: true,
      order: 7,
      category: 'identity',
    },
  },
  {
    key: 'provider-center',
    title: '服务商中心',
    path: '/user/provider/center',
    icon: 'Shop',
    permissions: [], // 🔧 修复：移除权限要求，允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '服务商认证' },
      { title: '服务商中心', current: true },
    ],
    meta: {
      description: '4标签企业管理中心，管理企业资料和服务配置',
      requiresAuth: true,
      order: 8,
      category: 'identity',
      isStatusDriven: true,
      hasTabPages: true,
    },
  },

  // 9-11. 模型业务管理 (3个独立页面)
  {
    key: 'model-upload',
    title: '模型上传',
    path: '/user/model-upload',
    icon: 'Upload',
    permissions: [], // 🔧 修复：对齐路由定义，允许所有登录用户查看，功能级权限由组件内部控制
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '模型业务管理' },
      { title: '模型上传', current: true },
    ],
    meta: {
      description: '新模型上传和发布管理',
      requiresAuth: true,
      order: 9,
      category: 'model',
      pageType: 'business',
    },
  },
  {
    key: 'my-models',
    title: '我的模型',
    path: '/user/my-models',
    icon: 'Box',
    permissions: [], // 🔧 修复：允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '模型业务管理' },
      { title: '我的模型', current: true },
    ],
    meta: {
      description: '已上传模型的管理和运营中心',
      requiresAuth: true,
      order: 10,
      category: 'model',
      pageType: 'business',
    },
  },
  {
    key: 'model-collection',
    title: '模型收藏',
    path: '/user/model-collection',
    icon: 'Star',
    permissions: [PERMISSIONS.USER.FAVORITES.VIEW], // 🔧 修复：使用统一权限常量
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '模型业务管理' },
      { title: '模型收藏', current: true },
    ],
    meta: {
      description: '收藏的模型管理，支持分类整理',
      requiresAuth: true,
      order: 11,
      category: 'model',
      pageType: 'business',
    },
  },

  // 12-14. 任务业务管理 (3个独立页面) - 集成分层权限
  {
    key: 'task-publish',
    title: '发布任务',
    path: '/user/task-publish',
    icon: 'EditPen',
    permissions: [], // 🔧 修复：对齐路由定义，允许所有登录用户查看，功能级权限由组件内部控制
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '任务业务管理' },
      { title: '发布任务', current: true },
    ],
    meta: {
      description: '众包任务发布，支持需求描述和条件筛选',
      requiresAuth: true,
      order: 12,
      category: 'task',
      pageType: 'business',
    },
  },
  {
    key: 'published-tasks',
    title: '发布管理',
    path: '/user/published-tasks',
    icon: 'List',
    permissions: [], // 🔧 修复：允许所有登录用户查看
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '任务业务管理' },
      { title: '发布管理', current: true },
    ],
    meta: {
      description: '已发布任务管理，包含进行中、已完成、已取消',
      requiresAuth: true,
      order: 13,
      category: 'task',
      pageType: 'business',
    },
  },
  {
    key: 'accepted-tasks',
    title: '承接管理',
    path: '/user/accepted-tasks',
    icon: 'Checked',
    permissions: [], // 🔧 修复：允许所有登录用户查看，分层权限在页面内部控制
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '任务业务管理' },
      { title: '承接管理', current: true },
    ],
    meta: {
      description: '承接任务管理和收益追踪，了解平台任务承接价值',
      requiresAuth: true,
      order: 14,
      category: 'task',
      pageType: 'business',
      // 集成分层权限配置
      layeredPermissions: {
        page: ['user:tasks:view'],
        functions: {
          'accept-task': ['user:tasks:accept'],
          'manage-task': ['user:tasks:manage'],
          'view-earnings': ['user:finance:earnings'],
        },
        guidance: {
          missingPermission: 'user:tasks:accept',
          upgradeMessage: '成为认证设计师或制造商即可承接任务获得收益',
          benefitPreview: [
            '承接平台众包任务',
            '获得任务完成收益',
            '查看收益统计报表',
            '享受优先任务推荐',
          ],
          upgradePaths: [
            {
              role: 'designer',
              path: '/user/designer',
              time: '5-15分钟',
              benefits: '设计任务收益',
            },
            {
              role: 'manufacturer',
              path: '/user/provider',
              time: '10-20分钟',
              benefits: '制造任务收益',
            },
          ],
        },
      },
    },
  },

  // 15-18. 财务业务管理 (4个独立页面) - 集成分层权限
  {
    key: 'account-overview',
    title: '账户总览',
    path: '/user/account-overview',
    icon: 'Wallet',
    permissions: [PERMISSIONS.USER.FINANCE.VIEW],
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '财务业务管理' },
      { title: '账户总览', current: true },
    ],
    meta: {
      description: '财务状况总览，包含余额、积分、收益统计',
      requiresAuth: true,
      order: 15,
      category: 'finance',
      pageType: 'business',
    },
  },
  {
    key: 'earnings-analysis',
    title: '收益分析',
    path: '/user/earnings-analysis',
    icon: 'TrendCharts',
    permissions: [PERMISSIONS.USER.FINANCE.VIEW], // 基础财务查看权限
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '财务业务管理' },
      { title: '收益分析', current: true },
    ],
    meta: {
      description: '平台收益潜力分析，了解认证用户收益机会',
      requiresAuth: true,
      order: 16,
      category: 'finance',
      pageType: 'business',
      // 集成分层权限配置
      layeredPermissions: {
        page: ['user:finance:view'],
        functions: {
          'detailed-analysis': ['user:finance:earnings'],
          'export-reports': ['user:finance:analytics'],
          'advanced-charts': ['user:finance:analytics'],
        },
        guidance: {
          missingPermission: 'user:finance:earnings',
          upgradeMessage: '成为认证用户即可查看详细收益分析和获得收益',
          benefitPreview: [
            '查看详细收益趋势图表',
            '分析收益来源和构成',
            '获得收益优化建议',
            '导出收益数据报告',
          ],
          upgradePaths: [
            {
              role: 'designer',
              path: '/user/designer',
              time: '5-15分钟',
              benefits: '设计任务收益',
            },
            {
              role: 'manufacturer',
              path: '/user/provider',
              time: '10-20分钟',
              benefits: '制造任务收益',
            },
          ],
        },
      },
    },
  },
  {
    key: 'transaction-records',
    title: '交易记录',
    path: '/user/transaction-records',
    icon: 'Document',
    permissions: [], // 🔧 修复：允许所有登录用户查看基础交易记录
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '财务业务管理' },
      { title: '交易记录', current: true },
    ],
    meta: {
      description: '完整交易历史记录，支持筛选、导出、对账',
      requiresAuth: true,
      order: 17,
      category: 'finance',
      pageType: 'business',
    },
  },
  {
    key: 'payment-withdraw',
    title: '充值提现',
    path: '/user/payment-withdraw',
    icon: 'Money',
    permissions: [PERMISSIONS.USER.FINANCE.VIEW], // 基础财务查看权限
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '财务业务管理' },
      { title: '充值提现', current: true },
    ],
    meta: {
      description: '资金充值和提现服务，了解平台资金流转机制',
      requiresAuth: true,
      order: 18,
      category: 'finance',
      pageType: 'business',
      // 集成分层权限配置
      layeredPermissions: {
        page: ['user:finance:view'],
        functions: {
          'recharge': ['user:finance:recharge'],
          'withdraw': ['user:finance:withdraw'],
          'withdraw-history': ['user:finance:records'],
        },
        guidance: {
          missingPermission: 'user:finance:withdraw',
          upgradeMessage: '完成认证后即可提现平台收益到个人账户',
          benefitPreview: [
            '多种提现方式支持',
            '快速到账服务',
            '提现手续费优惠',
            '专属财务管理支持',
          ],
          upgradePaths: [
            {
              role: 'designer',
              path: '/user/designer',
              time: '5-15分钟',
              benefits: '设计师提现特权',
            },
            {
              role: 'manufacturer',
              path: '/user/provider',
              time: '10-20分钟',
              benefits: '制造商财务服务',
            },
          ],
        },
      },
    },
  },

  // 19. 系统集成测试页面 (开发专用)
  {
    key: 'integration-test',
    title: '功能测试',
    path: '/user/integration-test',
    icon: 'DataAnalysis',
    permissions: [PERMISSIONS.USER.SYSTEM.TEST],
    breadcrumb: [
      { title: '用户中心', path: '/user' },
      { title: '系统功能' },
      { title: '功能测试', current: true },
    ],
    meta: {
      description: '19页面架构集成测试，验证路由、组件和功能完整性',
      requiresAuth: true,
      order: 19,
      category: 'system',
      pageType: 'development',
      isDevelopmentOnly: true,
    },
  },

  // ================================
  // 20-26. 服务中心导航项 (7个页面)
  // ================================

  // 帮助中心
  {
    key: 'service-help-faq',
    title: '常见问题',
    path: '/service/help/faq',
    icon: 'QuestionFilled',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '帮助中心' },
      { title: '常见问题', current: true },
    ],
    meta: {
      description: '平台使用常见问题解答',
      requiresAuth: false,
      order: 20,
      category: 'service',
    },
  },
  {
    key: 'service-help-guide',
    title: '使用指南',
    path: '/service/help/guide',
    icon: 'Document',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '帮助中心' },
      { title: '使用指南', current: true },
    ],
    meta: {
      description: '详细的平台使用指南',
      requiresAuth: false,
      order: 21,
      category: 'service',
    },
  },

  // 规则中心
  {
    key: 'service-rules-platform',
    title: '平台规则',
    path: '/service/rules/platform',
    icon: 'Document',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '规则中心' },
      { title: '平台规则', current: true },
    ],
    meta: {
      description: '平台使用规则和规范',
      requiresAuth: false,
      order: 22,
      category: 'service',
    },
  },
  {
    key: 'service-rules-agreement',
    title: '服务协议',
    path: '/service/rules/agreement',
    icon: 'Document',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '规则中心' },
      { title: '服务协议', current: true },
    ],
    meta: {
      description: '用户服务协议条款',
      requiresAuth: false,
      order: 23,
      category: 'service',
    },
  },

  // 联系我们
  {
    key: 'service-contact-chat',
    title: '在线客服',
    path: '/service/contact/chat',
    icon: 'ChatDotRound',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '联系我们' },
      { title: '在线客服', current: true },
    ],
    meta: {
      description: '与在线客服实时沟通',
      requiresAuth: false,
      order: 24,
      category: 'service',
    },
  },
  {
    key: 'service-contact-feedback',
    title: '意见反馈',
    path: '/service/contact/feedback',
    icon: 'Message',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '联系我们' },
      { title: '意见反馈', current: true },
    ],
    meta: {
      description: '提交产品意见和建议',
      requiresAuth: false,
      order: 25,
      category: 'service',
    },
  },
  {
    key: 'service-contact-info',
    title: '联系方式',
    path: '/service/contact/info',
    icon: 'Phone',
    permissions: [], // 无权限要求，所有用户可访问
    breadcrumb: [
      { title: '服务中心', path: '/service' },
      { title: '联系我们' },
      { title: '联系方式', current: true },
    ],
    meta: {
      description: '获取官方联系方式',
      requiresAuth: false,
      order: 26,
      category: 'service',
    },
  },
]

/**
 * 导航分组配置映射
 */
export const NAVIGATION_GROUPS: Record<string, NavigationGroup> = {
  workspace: {
    key: 'workspace',
    title: '工作台',
    icon: 'House',
    order: 1,
    description: '个人工作台和数据概览',
  },
  account: {
    key: 'account',
    title: '个人账号管理',
    icon: 'User',
    order: 2,
    description: '个人信息和账号设置',
  },
  identity: {
    key: 'identity',
    title: '身份认证管理',
    icon: 'CreditCard',
    order: 3,
    description: '设计师和服务商认证',
  },
  model: {
    key: 'model',
    title: '模型业务管理',
    icon: 'Box',
    order: 4,
    description: '3D模型上传和管理',
  },
  task: {
    key: 'task',
    title: '任务业务管理',
    icon: 'List',
    order: 5,
    description: '众包任务发布和承接',
  },
  finance: {
    key: 'finance',
    title: '财务业务管理',
    icon: 'Wallet',
    order: 6,
    description: '收益分析和资金管理',
  },
  system: {
    key: 'system',
    title: '系统功能',
    icon: 'Setting',
    order: 7,
    description: '系统设置和调试工具',
  },
  service: {
    key: 'service',
    title: '服务中心',
    icon: 'Service',
    order: 8,
    description: '帮助中心、规则中心、联系我们',
  },
} as const

/**
 * 获取过滤后的导航配置（生产环境移除开发专用页面）
 */
export function getFilteredNavigation(): SidebarNavigationItem[] {
  if (import.meta.env.DEV) {
    return SIDEBAR_USER_NAVIGATION
  }

  return SIDEBAR_USER_NAVIGATION.filter(item => !item.meta.isDevelopmentOnly)
}

/**
 * 获取指定分类的导航项
 */
export function getNavigationByCategory(category: string): SidebarNavigationItem[] {
  return getFilteredNavigation().filter(item => item.meta.category === category)
}

/**
 * 获取具有分层权限配置的导航项
 */
export function getLayeredPermissionNavigation(): SidebarNavigationItem[] {
  return getFilteredNavigation().filter(item => !!item.meta.layeredPermissions)
}

/**
 * 按路径查找导航项
 */
export function findNavigationByPath(path: string): SidebarNavigationItem | null {
  return getFilteredNavigation().find(item => item.path === path) || null
}

/**
 * 按key查找导航项
 */
export function findNavigationByKey(key: string): SidebarNavigationItem | null {
  return getFilteredNavigation().find(item => item.key === key) || null
}

/**
 * 获取所有导航路径
 */
export function getAllNavigationPaths(): string[] {
  return getFilteredNavigation().map(item => item.path)
}

/**
 * 导出类型定义
 */
// 类型在文件内已声明并导出，避免重复导出冲突
